import { PUBLIC_DO_ASSETS_URL } from '$env/static/public';

import { redirect } from '@sveltejs/kit';
import { error } from '@sveltejs/kit';

import { AuctionStatus, MAX_LIMIT, Status } from '@do/models';

import { extendBreadcrumbsWithSlug } from '@shared/ui/breadcrumbs';
import { parseSlug } from '@shared/utils';

import { ArtAssetCardApi } from '@entities/art-asset';
import { mapDataToIU } from '@entities/artist';
import { ArtistApi } from '@entities/artist';
import { AuctionApi } from '@entities/auction';
import { SelectionApi } from '@entities/selection';
import { slugLibs } from '@entities/slug';

import { artistPageLibs } from '@pages/artist';
import { ARTWORKS_PER_PAGE } from '@pages/artworks';
import { SELECTIONS_PER_PAGE } from '@pages/selections/configs';

import type { PageLoad } from './$types';

export const ssr = true;

export const load: PageLoad = async ({ data, fetch, params }) => {
  const {
    form,
    id,
    slug,
    newLoadMore,
    artworksSortColumn,
    artworksSortOrder,
    extraPath,
    filters,
    configurationsId,
    configurationsType,
    configurationsName,
  } = data;

  const artistApi = new ArtistApi(fetch);
  const auctionApi = new AuctionApi(fetch);
  const artworksApi = new ArtAssetCardApi(fetch);
  const selectionApi = new SelectionApi(fetch);

  const auctionsParams = new URLSearchParams();
  auctionsParams.append('offset', '0');
  auctionsParams.append('limit', MAX_LIMIT.toString());
  auctionsParams.append('institution_id', configurationsId);
  auctionsParams.append('artists', id);
  auctionsParams.append('statuses', `${AuctionStatus.Active},${AuctionStatus.Finalizing}`);

  const parsedParams = parseSlug(slug);

  const page = +parsedParams.page > 0 ? +parsedParams.page : 1; // get only number from page param

  const selectionsParams = new URLSearchParams();
  selectionsParams.append('offset', `${(page - 1) * SELECTIONS_PER_PAGE}`);
  selectionsParams.append('limit', `${SELECTIONS_PER_PAGE * (newLoadMore + 1)}`);
  selectionsParams.append('institution_id', configurationsId);
  selectionsParams.append('artists', id);

  const [
    artist,
    { artworks, totalCount: artworksCount },
    { auctions, auctionsCount },
    { selections, total_count: selectionsCount },
  ] = await Promise.all([
    artistApi.readOne(id).then((response) => {
      if (response.status !== Status.Published) {
        throw redirect(302, '/artists');
      }
      return mapDataToIU(response);
    }),
    artworksApi.readMany({
      ...parsedParams,
      page,
      loadMore: newLoadMore,
      perPage: ARTWORKS_PER_PAGE,
      offset: (page - 1) * ARTWORKS_PER_PAGE,
      institutionId: Number(configurationsId),
      artists: id,
      sort: artworksSortColumn,
      order: artworksSortOrder,
    }),
    auctionApi.readMany(auctionsParams),
    selectionApi.readMany(selectionsParams).catch((error) => {
      console.error('Error fetching selections:', error);
      return { selections: [], total_count: 0 };
    }),
  ]);

  if (!artist.id) {
    throw error(404, { message: 'Artist not found' });
  }

  slugLibs.handleSlugMismatch({
    currentSlug: params.name,
    entitySlug: artist.slug,
    redirectPath: `/artist-${artist.id}-${artist.slug}/`,
  });

  const defaultPreset = artist.photo?.asset_transformations?.find(
    (preset) => preset.preset_key === 'default-1500w-webp' || preset.preset_key === 'default-500w-webp',
  );

  const baseBreadcrumbs = [
    {
      name: 'Home',
      slug: '/',
      canonical: false,
    },
    {
      name: 'Artists',
      slug: '/artists/',
      canonical: false,
    },
    {
      name: artist.name,
      slug: `/artist-${artist.id}-${artist.slug}/`,
      canonical: !extraPath,
    },
  ];

  const breadcrumbs = extendBreadcrumbsWithSlug({
    baseBreadcrumbs,
    name: `Page ${page}`,
    slug: extraPath,
  });

  const seoData = new artistPageLibs.Seo(
    configurationsType,
    { artistName: artist.name, galleryName: configurationsName, page },
    {
      metaImage: {
        alt: artist.name,
        caption: artist.name,
        url: `${PUBLIC_DO_ASSETS_URL}/${defaultPreset?.transformation_name}`,
        width: artist.photo?.width ?? 0,
        height: artist.photo?.height ?? 0,
      },
      breadcrumbs,
      robots: !filters,
    },
  );

  const seo = seoData.generateSeoConfig();

  return {
    artist,
    auctions,
    artworks,
    artworksCount,
    page,
    newLoadMore,
    auctionsCount,
    form,
    seo,
    hasParams: !!slug,
    selections,
    selectionsCount,
  };
};
